import { useCallback, useRef } from "react";

const LONG_PRESS_MS = 450;
// A press that drifts further than this is the start of a scroll, not a hold.
const MOVE_TOLERANCE_PX = 10;

/**
 * Press-and-hold on touch, with a plain click left intact.
 *
 * Pointer events cover mouse, touch and pen in one path. The two things worth
 * knowing: a touch that moves is the user scrolling the list, so the timer has
 * to be cancelled on drift or every scroll would trip a long press; and once a
 * hold has fired, the click that the browser sends afterwards has to be
 * swallowed, or the row would both enter selection mode and open the editor.
 */
export function useLongPress(onLongPress, onClick, { disabled = false } = {}) {
  const timer = useRef(null);
  const origin = useRef(null);
  const fired = useRef(false);

  const cancel = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
    origin.current = null;
  }, []);

  const onPointerDown = useCallback((event) => {
    if (disabled || event.button === 2) return;
    fired.current = false;
    origin.current = { x: event.clientX, y: event.clientY };
    timer.current = setTimeout(() => {
      fired.current = true;
      timer.current = null;
      onLongPress?.(event);
    }, LONG_PRESS_MS);
  }, [disabled, onLongPress]);

  const onPointerMove = useCallback((event) => {
    if (!origin.current || !timer.current) return;
    const drift = Math.hypot(event.clientX - origin.current.x, event.clientY - origin.current.y);
    if (drift > MOVE_TOLERANCE_PX) cancel();
  }, [cancel]);

  const handleClick = useCallback((event) => {
    if (fired.current) {
      fired.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  }, [onClick]);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: cancel,
    onPointerLeave: cancel,
    onPointerCancel: cancel,
    onContextMenu: (event) => {
      // Long-press on mobile Safari/Chrome raises a context menu over the row.
      if (fired.current) event.preventDefault();
    },
    onClick: handleClick,
  };
}
